/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  CardHeader,
  Form,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import { DetailHeader } from '../../Components/Common/Detail';
import { confirmAction } from '../../utils/confirm';
import notify from '../../utils/notify';
import axios from 'axios';
import { api } from '../../config';
import { PasswordField, RequiredNote } from '../../Components/Form';
import ActionButton from '../../Components/Common/ActionButton';
import { Skeleton, SkeletonCard } from '../../Components/Common/Skeleton';
import useAsyncAction from '../../Components/Hooks/useAsyncAction';
import UserFormModal from './component/UserFormModal';
import { formatDateTime } from '../../utils/format';

const UserDetailPage = () => {
  const [ids] = useState(useParams().id);
  const navigate = useNavigate();
  const token = useSelector(state => state.Login?.user?.token);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  const lockStatus = useMemo(() => {
    if (!user || !user.lockoutEnd) {
      return { isLocked: false, isPermanent: false };
    }
    const lockoutDate = new Date(user.lockoutEnd);
    const now = new Date();
    const isLocked = lockoutDate > now;
    const isPermanent = isLocked && lockoutDate.getFullYear() > 2100;
    return { isLocked, isPermanent };
  }, [user]);

  const fetchUserDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(`${api.API_URL}/appuser/${ids}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      console.log('Raw API response:', res);
      console.log('User data extracted:', res.data?.data || res.data);

      setUser(res);
    } catch (err) {
      console.error('fetchUserDetail error:', err);
      const errorMsg =
        typeof err === 'string'
          ? err
          : err?.message || 'Failed to fetch user details';
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        setError('Session expired. Please login again.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [ids, token]);

  const handleEditSuccess = useCallback(() => {
    fetchUserDetail();
  }, [fetchUserDetail]);

  useEffect(() => {
    if (ids) fetchUserDetail();
  }, [fetchUserDetail]);

  const handleLockUnlock = async action => {
    const isLock = action === 'lock';
    const confirmed = await confirmAction({
      tone: isLock ? 'danger' : 'primary',
      icon: isLock ? 'ri-lock-2-line' : 'ri-lock-unlock-line',
      title: isLock ? 'Lock this user account?' : 'Unlock this user account?',
      message: isLock
        ? 'The user will not be able to sign in until the account is unlocked again.'
        : 'The user will regain access to the system immediately.',
      subject: user?.displayName || user?.userName,
      confirmLabel: isLock ? 'Lock account' : 'Unlock account',
    });

    if (confirmed) {
      setActionLoading(true);
      try {
        let body = {};
        if (isLock) {
          const expiryDate = new Date();
          expiryDate.setFullYear(expiryDate.getFullYear() + 100);
          body = { lockoutEnd: expiryDate.toISOString() };
        }

        const res = await axios.post(
          `${api.API_URL}/appuser/${ids}/${action}`,
          body,
          {
            withXSRFToken: true,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
            },
          }
        );

        if (res.status === 200 || res.data?.success) {
          notify.success(`User account ${action}ed successfully.`);
          fetchUserDetail();
        } else {
          notify.error(
            res.data?.message ||
              `Failed to ${action} the user account. Please try again.`
          );
        }
      } catch (e) {
        notify.apiError(e, `${action} the user account`);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleChangePassword = async () => {
    const currentPassword = passwordForm.currentPassword || '';
    const newPassword = passwordForm.newPassword || '';
    const errors = {};

    if (!currentPassword)
      errors.currentPassword = 'Current password is required';

    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasNonAlphanumeric = /[^A-Za-z0-9]/.test(newPassword);

    if (!newPassword) errors.newPassword = 'New password is required';
    else if (newPassword.length < 8)
      errors.newPassword = 'Password must be at least 8 characters';
    else if (!hasUppercase)
      errors.newPassword = 'Password needs an uppercase letter';
    else if (!hasNonAlphanumeric)
      errors.newPassword = 'Password needs a symbol (e.g. ! @ # $)';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setActionLoading(true);
    try {
      const res = await axios.post(
        `${api.API_URL}/appuser/${ids}/change-password`,
        {
          userId: ids,
          currentPassword: currentPassword,
          newPassword: newPassword,
        },
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      if (res.status === 200 || res.data?.success) {
        setShowPasswordModal(false);
        setPasswordForm({ currentPassword: '', newPassword: '' });
        setFormErrors({});
        notify.success('Password changed successfully.');
      } else {
        notify.error(
          res.data?.message ||
            'Failed to change the password. Please try again.'
        );
      }
    } catch (e) {
      notify.apiError(e, 'change the password');
    } finally {
      setActionLoading(false);
    }
  };

  const [submitChangePassword, changingPassword] =
    useAsyncAction(handleChangePassword);

  document.title = user
    ? `${user.displayName || user.userName} - Profile`
    : 'User Profile';

  // Only the first load replaces the page; refetches after an edit or a
  // lock/unlock keep the profile on screen.
  if (loading && !user) {
    return (
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='User Profile' pageTitle='User Management' />
          <Card>
            <CardBody>
              <div className='d-flex align-items-center gap-3'>
                <Skeleton circle width={96} height={96} />
                <div className='flex-grow-1'>
                  <Skeleton width='30%' height={22} />
                  <Skeleton className='mt-2' width='20%' height={14} />
                  <Skeleton className='mt-2' width='40%' height={14} />
                </div>
              </div>
            </CardBody>
          </Card>
          <Row>
            <Col xxl={4}>
              <SkeletonCard lines={6} className='mb-3' />
              <SkeletonCard lines={2} className='mb-3' />
            </Col>
            <Col xxl={8}>
              <SkeletonCard lines={8} />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const isActualError = error || (!loading && !user);

  if (isActualError) {
    const isUnauthorized =
      error?.includes('401') ||
      error?.includes('Unauthorized') ||
      error?.includes('Session expired');
    return (
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb
            title={isUnauthorized ? 'Session Expired' : 'Error'}
            pageTitle='User Management'
          />
          <div className='text-center py-5'>
            {isUnauthorized ? (
              <>
                <i className='ri-lock-password-line display-1 text-danger'></i>
                <h4 className='mt-4'>{error || 'Session Expired'}</h4>
                <ActionButton
                  variant='primary'
                  icon='ri-login-box-line'
                  className='mt-3'
                  onClick={() => (window.location.href = '/login')}
                >
                  Login Again
                </ActionButton>
              </>
            ) : (
              <>
                <i className='ri-error-warning-line display-1 text-warning'></i>
                <h4 className='mt-4'>User Not Found</h4>
                <Alert color='danger' className='d-inline-block mt-2'>
                  {error || 'User data is missing'}
                </Alert>
                <div className='mt-3'>
                  <Link to='/apps-usermanagement'>
                    <ActionButton variant='primary' icon='ri-arrow-left-line'>
                      Back to List
                    </ActionButton>
                  </Link>
                </div>
              </>
            )}
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className='page-content'>
      <Container fluid>
        <PageBreadcrumb
          fallback={{
            label: 'User Management',
            to: '/apps-usermanagement/view',
          }}
          current={user?.displayName || user?.userName || 'User Profile'}
          listClassName='p-3 py-2 bg-light'
        />

        <DetailHeader
          onBack={() => navigate('/apps-usermanagement/view')}
          backLabel='Back to list'
          eyebrow={
            <>
              <i className='ri-user-3-line' aria-hidden='true' />
              <span>{user?.userName}</span>
            </>
          }
          title={user?.displayName || user?.userName}
          subtitle={user?.jobTitle || 'No Position'}
          badges={
            <>
              {lockStatus.isLocked ? (
                <Badge color='danger' className='px-2 py-1'>
                  <i className='ri-lock-2-line me-1'></i>
                  LOCKED
                </Badge>
              ) : (
                <Badge color='success' className='px-2 py-1'>
                  <i className='ri-checkbox-circle-line me-1'></i>
                  ACTIVE
                </Badge>
              )}
            </>
          }
          actions={
            <>
              <div className='d-none d-lg-flex gap-2'>
                <ActionButton
                  variant='secondary'
                  icon='ri-lock-password-line'
                  onClick={() => {
                    setFormErrors({});
                    setPasswordForm({
                      currentPassword: '',
                      newPassword: '',
                    });
                    setShowPasswordModal(true);
                  }}
                >
                  Change Password
                </ActionButton>
                {lockStatus.isLocked ? (
                  <ActionButton
                    variant='secondary'
                    icon='ri-lock-unlock-line'
                    onClick={() => handleLockUnlock('unlock')}
                    disabled={actionLoading}
                  >
                    Unlock
                  </ActionButton>
                ) : (
                  <ActionButton
                    variant='destructive'
                    icon='ri-lock-2-line'
                    confirm={false}
                    onClick={() => handleLockUnlock('lock')}
                    disabled={actionLoading}
                  >
                    Lock
                  </ActionButton>
                )}
                <ActionButton
                  variant='primary'
                  icon='ri-edit-box-line'
                  onClick={() => setShowEditModal(true)}
                >
                  Edit Profile
                </ActionButton>
              </div>

              <UncontrolledDropdown className='d-lg-none'>
                <DropdownToggle color='primary' caret>
                  <i className='ri-more-2-line'></i> Actions
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem
                    onClick={() => {
                      setFormErrors({});
                      setPasswordForm({
                        currentPassword: '',
                        newPassword: '',
                      });
                      setShowPasswordModal(true);
                    }}
                  >
                    <i className='ri-lock-password-line me-2'></i> Change
                    Password
                  </DropdownItem>
                  <DropdownItem
                    onClick={() =>
                      handleLockUnlock(lockStatus.isLocked ? 'unlock' : 'lock')
                    }
                    disabled={actionLoading}
                  >
                    <i
                      className={`${
                        lockStatus.isLocked
                          ? 'ri-lock-unlock-line'
                          : 'ri-lock-2-line'
                      } me-2`}
                    ></i>
                    {lockStatus.isLocked ? 'Unlock User' : 'Lock User'}
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem onClick={() => setShowEditModal(true)}>
                    <i className='ri-edit-box-line me-2'></i> Edit Profile
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </>
          }
          facts={[
            {
              icon: 'ri-shield-user-line',
              label: 'Roles',
              value: user?.roles?.join(', ') || 'No Role',
            },
            {
              icon: 'ri-building-2-line',
              label: 'Organization',
              value:
                user?.roles && user.roles.length > 0
                  ? user.roles.some(
                      r => r.toUpperCase() === 'REGISTERED SUPPLIER'
                    )
                    ? 'MITRA'
                    : 'PERTAMINA'
                  : null,
            },
            { icon: 'ri-mail-line', label: 'Email', value: user?.email },
            {
              icon: 'ri-organization-chart',
              label: 'Division',
              value: user?.division,
            },
          ]}
        />

        <Row>
          <Col lg={12}>
            <Row>
              {/* Sidebar */}
              <Col xxl={4}>
                {/* Info Card */}
                <Card>
                  <CardBody>
                    <h5 className='card-title mb-3'>Info</h5>
                    <div className='table-responsive'>
                      <Table className='table-borderless mb-0'>
                        <tbody>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Username :
                            </th>
                            <td className='text-muted'>{user?.userName}</td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Email :
                            </th>
                            <td className='text-muted'>
                              {user?.email}
                              {user?.emailConfirmed && (
                                <i className='ri-verified-badge-line text-success ms-2'></i>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Employee No :
                            </th>
                            <td className='text-muted'>
                              {user?.employeeNo || '-'}
                            </td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Division :
                            </th>
                            <td className='text-muted'>
                              {user?.division || '-'}
                            </td>
                          </tr>
                          <tr>
                            <th className='ps-0' scope='row'>
                              Type :
                            </th>
                            <td className='text-muted'>
                              {user?.employeeType || '-'}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>

                {/* Security Card */}
                <Card>
                  <CardBody>
                    <h5 className='card-title mb-4'>Security</h5>

                    <div className='mb-3'>
                      <p className='text-muted mb-1'>Failed Attempts</p>
                      <h6 className='mb-0'>{user?.accessFailedCount || 0}</h6>
                    </div>
                    <div>
                      <p className='text-muted mb-1'>Lockout Until</p>
                      <h6 className='mb-0'>
                        {!user?.lockoutEnd ||
                        new Date(user.lockoutEnd) <= new Date()
                          ? 'Not Locked'
                          : new Date(user.lockoutEnd).getFullYear() > 2100
                            ? 'Permanently Locked'
                            : formatDateTime(user.lockoutEnd)}
                      </h6>
                    </div>
                  </CardBody>
                </Card>

                {/* Audit Trail */}
                <Card>
                  <CardHeader>
                    <h5 className='card-title mb-0'>Audit Trail</h5>
                  </CardHeader>
                  <CardBody>
                    <div className='profile-timeline'>
                      <div
                        className='accordion accordion-flush'
                        id='auditAccordion'
                      >
                        <div className='accordion-item border-0'>
                          <div className='accordion-header'>
                            <button
                              className='accordion-button p-2 shadow-none'
                              type='button'
                            >
                              <div className='d-flex'>
                                <div className='flex-shrink-0 avatar-xs'>
                                  <div className='avatar-title bg-success-subtle text-success rounded-circle'>
                                    <i className='ri-add-circle-line'></i>
                                  </div>
                                </div>
                                <div className='flex-grow-1 ms-3'>
                                  <h6 className='fs-14 mb-1'>
                                    Account Created
                                  </h6>
                                  <small className='text-muted'>
                                    Created on{' '}
                                    {user?.createdDate
                                      ? formatDateTime(user.createdDate)
                                      : '-'}
                                  </small>
                                </div>
                              </div>
                            </button>
                          </div>
                          <div className='accordion-collapse collapse show'>
                            <div className='accordion-body ms-2 ps-5'>
                              User account was created by{' '}
                              <strong>{user?.createdBy || 'System'}</strong>
                            </div>
                          </div>
                        </div>

                        {user?.updatedDate && (
                          <div className='accordion-item border-0'>
                            <div className='accordion-header'>
                              <button
                                className='accordion-button p-2 shadow-none'
                                type='button'
                              >
                                <div className='d-flex'>
                                  <div className='flex-shrink-0 avatar-xs'>
                                    <div className='avatar-title bg-warning-subtle text-warning rounded-circle'>
                                      <i className='ri-edit-circle-line'></i>
                                    </div>
                                  </div>
                                  <div className='flex-grow-1 ms-3'>
                                    <h6 className='fs-14 mb-1'>Last Updated</h6>
                                    <small className='text-muted'>
                                      Modified on{' '}
                                      {formatDateTime(user.updatedDate)}
                                    </small>
                                  </div>
                                </div>
                              </button>
                            </div>
                            <div className='accordion-collapse collapse show'>
                              <div className='accordion-body ms-2 ps-5'>
                                Profile was last modified by{' '}
                                <strong>{user?.updatedBy || 'System'}</strong>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Main Content */}
              <Col xxl={8}>
                {/* About Card */}
                <Card>
                  <CardBody>
                    <h5 className='card-title mb-3'>About</h5>
                    <p>
                      {user?.displayName} is a{' '}
                      {user?.employeeType || 'employee'} working as{' '}
                      {user?.jobTitle || 'staff member'} in{' '}
                      {user?.division || 'the organization'}.
                    </p>
                    <Row>
                      <Col xs={6} md={6}>
                        <div className='d-flex mt-4'>
                          <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                            <div className='avatar-title bg-light rounded-circle fs-16 text-primary material-shadow'>
                              <i className='ri-user-2-line'></i>
                            </div>
                          </div>
                          <div className='flex-grow-1 overflow-hidden'>
                            <p className='mb-1 text-muted small'>Full Name :</p>
                            <h6 className='text-truncate mb-0 fw-bold'>
                              {user?.displayName}
                            </h6>
                          </div>
                        </div>
                      </Col>
                      <Col xs={6} md={6}>
                        <div className='d-flex mt-4'>
                          <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                            <div className='avatar-title bg-light rounded-circle fs-16 text-primary material-shadow'>
                              <i className='ri-briefcase-line'></i>
                            </div>
                          </div>
                          <div className='flex-grow-1 overflow-hidden'>
                            <p className='mb-1 text-muted small'>
                              Designation :
                            </p>
                            <h6 className='text-truncate mb-0 fw-bold'>
                              {user?.jobTitle || '-'}
                            </h6>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div className='d-flex mt-4 border-top pt-3'>
                          <div className='flex-shrink-0 avatar-xs align-self-center me-3'>
                            <div className='avatar-title bg-light rounded-circle fs-16 text-primary material-shadow'>
                              <i className='ri-map-pin-line'></i>
                            </div>
                          </div>
                          <div className='flex-grow-1'>
                            <p className='mb-1 text-muted small'>Address :</p>
                            <h6
                              className='mb-0 fw-bold'
                              style={{
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                              }}
                            >
                              {user?.address || '-'}
                            </h6>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Assignments */}
                <Card>
                  <CardBody>
                    <h5 className='card-title mb-4'>Assignments</h5>
                    <div className='mb-3'>
                      <h6 className='fs-14 mb-2'>Roles</h6>
                      <div className='d-flex flex-wrap gap-2'>
                        {user?.roles && user.roles.length > 0 ? (
                          <div className='d-flex flex-wrap gap-2'>
                            {user.roles.map((role, idx) => (
                              <div
                                key={idx}
                                className='border rounded p-2 d-flex align-items-center bg-light'
                                style={{
                                  minWidth: 'min(250px, 100%)',
                                  flex: '1 1 calc(50% - 1rem)',
                                }}
                              >
                                <div className='avatar-sm flex-shrink-0'>
                                  <span className='avatar-title bg-primary text-white rounded fs-18'>
                                    <i className='ri-shield-user-line'></i>
                                  </span>
                                </div>
                                <div className='flex-grow-1 ms-3 overflow-hidden'>
                                  <div className='fw-bold fs-13 text-truncate'>
                                    {role}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className='text-center p-3 border border-dashed rounded w-100'>
                            <span className='text-muted fst-italic fs-12'>
                              - No Role Assigned -
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h6 className='fs-14 mb-2'>Positions</h6>
                      <div className='d-flex flex-wrap gap-2'>
                        {user?.userPositions &&
                        user.userPositions.length > 0 ? (
                          user.userPositions.map((pos, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-2 d-flex align-items-center bg-light'
                              style={{
                                minWidth: 'min(250px, 100%)',
                                flex: '1 1 calc(50% - 1rem)',
                              }}
                            >
                              <div className='avatar-sm flex-shrink-0'>
                                <span className='avatar-title bg-warning text-white rounded fs-18'>
                                  <i className='ri-briefcase-line me-2'></i>
                                </span>
                              </div>
                              <div className='flex-grow-1 ms-3 overflow-hidden'>
                                <div className='fw-bold fs-13 text-truncate'>
                                  {pos.positionName}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <span className='text-muted fst-italic'>
                            - No Position Assigned -
                          </span>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Change Password Modal */}
        <Modal
          isOpen={showPasswordModal}
          toggle={() => setShowPasswordModal(false)}
          centered
        >
          <ModalHeader toggle={() => setShowPasswordModal(false)}>
            Change Password for {user?.userName}
          </ModalHeader>
          <ModalBody>
            <Form
              onSubmit={e => {
                e.preventDefault();
                submitChangePassword();
              }}
            >
              <RequiredNote />
              <PasswordField
                label='Current password'
                name='currentPassword'
                required
                placeholder='Enter current password'
                value={passwordForm.currentPassword}
                error={formErrors.currentPassword}
                onChange={e => {
                  setPasswordForm({
                    ...passwordForm,
                    currentPassword: e.target.value,
                  });
                  if (formErrors.currentPassword)
                    setFormErrors({ ...formErrors, currentPassword: '' });
                }}
              />
              <div className='mt-3'>
                <PasswordField
                  label='New password'
                  name='newPassword'
                  required
                  autoComplete='new-password'
                  hint='At least 8 characters, one uppercase letter and one symbol'
                  value={passwordForm.newPassword}
                  error={formErrors.newPassword}
                  onChange={e => {
                    setPasswordForm({
                      ...passwordForm,
                      newPassword: e.target.value,
                    });
                    if (formErrors.newPassword)
                      setFormErrors({ ...formErrors, newPassword: '' });
                  }}
                />
              </div>
            </Form>
          </ModalBody>
          <ModalFooter>
            <ActionButton
              variant='secondary'
              icon='ri-close-line'
              onClick={() => setShowPasswordModal(false)}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant='primary'
              icon='ri-lock-password-line'
              onClick={submitChangePassword}
              loading={actionLoading || changingPassword}
            >
              Update Password
            </ActionButton>
          </ModalFooter>
        </Modal>

        <UserFormModal
          isOpen={showEditModal}
          toggle={() => {
            setShowEditModal(false);
            fetchUserDetail();
          }}
          userData={user}
          onSuccess={handleEditSuccess}
        />
      </Container>
    </div>
  );
};

export default UserDetailPage;
